import { css, cx } from "@emotion/css";
import {
  Box,
  Collapse,
  Colors,
  Icons,
  Table,
  tableCellClasses,
  tableRowClasses,
  Typography,
  useMediaQuery,
} from "@analyticsMD/quil";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  styles as commonStyles,
  sxStyles as commonSxStyles,
} from "./common-styles";
import { FC, useContext, useMemo, useState } from "react";
import APIContext from "../../contexts/APIContext";
import SectionGrid from "../section-grid";
import Configuration from "../../types/Configuration";
import moment from "moment";
import {
  clinicalInsightPrimaryValue,
  clinicalInsightTimestampRaw,
  sortClinicalInsights,
} from "../../utils/clinical-insight-display";

const styles = {
  ...commonStyles,
  insightsSection: css`
    background-color: ${Colors.white};
    padding: 0 1rem;
  `,
  expandCollapseIcon: css`
    color: ${Colors.grey_40};
  `,
  tableLastColAlignment: css`
    & .MuiTableCell-root:last-of-type {
      width: 25%;
      text-align: right;
    }
    @media (min-width: 700px) {
      & .MuiTableCell-root:last-of-type {
        width: 18%;
      }
    }
    @media (min-width: 900px) {
      & .MuiTableCell-root:last-of-type {
        width: 20%;
      }
    }
  `,
  tableRow: css`
    pointer-events: none;
  `,
};

const sxStyles = {
  ...commonSxStyles,
  table: {
    [`& .${tableRowClasses.root}`]: {
      [`& .${tableCellClasses.root}:first-of-type`]: {
        paddingLeft: "0",
      },
      [`& .${tableCellClasses.root}`]: {
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
        textAlign: "left",
        textAlignLast: "left",
        paddingLeft: "0.3rem",
      },
    },
  },
  icon: {
    color: Colors.good_60,
    paddingRight: "1rem",
  },
  key: {
    fontSize: "0.831rem",
    fontWeight: "bold",
    color: Colors.grey_65,
  },
  value: {
    fontSize: "0.769rem",
    color: Colors.grey_65,
    marginLeft: "1rem",
  },
  secondaryValue: {
    fontSize: ".65rem",
    textAlign: "right",
    color: Colors.grey_65,
  },
  valueWrapper: { display: "flex", justifyContent: "left" },
};

interface ClinicalInsightTableRow {
  id: number;
  sort_order: number;
  milestone_display_name: string;
  formatted_value: string;
  timestamp_raw?: string;
}

interface ClinicalInsightsSectionProps {
  configuration: Configuration;
}

const ClinicalInsightsSection: FC<ClinicalInsightsSectionProps> = ({
  configuration,
}) => {
  const { patientClinicalInsightsQuery } = useContext(APIContext);

  const tableRows: ClinicalInsightTableRow[] = useMemo(() => {
    if (!patientClinicalInsightsQuery.isSuccess) return [];
    return sortClinicalInsights(patientClinicalInsightsQuery.data).map(
      (insight) => ({
        id: insight.id,
        sort_order: insight.milestone.sort_order,
        milestone_display_name: insight.milestone.display_name,
        formatted_value: clinicalInsightPrimaryValue(insight),
        timestamp_raw: clinicalInsightTimestampRaw(insight),
      }),
    );
  }, [
    patientClinicalInsightsQuery.isSuccess,
    patientClinicalInsightsQuery.data,
  ]);

  const defaultSortColumn = "sort_order";
  const columns = [
    {
      headerName: "",
      field: "milestone_display_name",
      renderCell: (row: ClinicalInsightTableRow) => (
        <Box sx={sxStyles.valueWrapper}>
          <Typography sx={sxStyles.key}>
            {row.milestone_display_name}
          </Typography>
        </Box>
      ),
    },
    {
      headerName: "",
      field: "formatted_value",
      renderCell: (row: ClinicalInsightTableRow) => (
        <Box sx={sxStyles.valueWrapper}>
          <Typography sx={sxStyles.value}>{row.formatted_value}</Typography>
        </Box>
      ),
    },
    {
      headerName: "",
      field: "timestamp_raw",
      renderCell: (row: ClinicalInsightTableRow) => {
        const ts = row.timestamp_raw;
        const tsMoment = ts ? moment.utc(ts) : null;
        const label =
          tsMoment && tsMoment.isValid() ? tsMoment.format("MM/DD/YYYY") : "";
        return <Box sx={sxStyles.secondaryValue}>{label}</Box>;
      },
    },
  ];

  const renderInsightsSection = () => {
    if (tableRows.length > 0) {
      return (
        <Box sx={{ marginTop: "0.4rem" }}>
          <Table
            columns={columns}
            rows={tableRows}
            sx={sxStyles.table}
            defaultSortColumn={defaultSortColumn}
            hideHeader
            className={styles.tableLastColAlignment}
            rowClass={styles.tableRow}
          />
        </Box>
      );
    }
    return (
      <div className={styles.emptyWrapper}>
        <Icons.PlaceholderComplete className={styles.emptyImg} />
        <span className={styles.emptyMsg}>
          There are currently no clinical insights to display
        </span>
      </div>
    );
  };

  const useWideLayout = useMediaQuery("(min-width:62.5rem)", { noSsr: true });
  const [showSection, setShowSection] = useState(
    useWideLayout || !configuration.insights_section_enabled,
  );

  return (
    <Box className={cx(styles.roundedSection, styles.insightsSection)}>
      <Box
        sx={sxStyles.headingWrapper}
        onClick={() => setShowSection(!showSection)}
      >
        <Typography sx={sxStyles.sectionHeading}>
          <AutoAwesomeOutlinedIcon
            sx={{
              color: Colors.white,
              backgroundColor: Colors.qpink_40,
              padding: "0.1rem",
              borderRadius: "0.25rem",
              marginRight: "0.5rem",
              fontSize: "1rem",
            }}
          />
          <span>Clinical Insights</span>
        </Typography>
        {!showSection && (
          <ExpandMoreIcon className={styles.expandCollapseIcon} />
        )}
        {showSection && (
          <ExpandLessIcon className={styles.expandCollapseIcon} />
        )}
      </Box>

      <Collapse in={showSection} unmountOnExit mountOnEnter>
        <SectionGrid>{renderInsightsSection()}</SectionGrid>
      </Collapse>
    </Box>
  );
};

export default ClinicalInsightsSection;
